import type { TrialStatus } from '@st/contract';

/**
 * Probni pristup: prvih N treninga programa je besplatno, ostatak se otključava
 * kupnjom. Server je jedini koji zna koliko ih je iskorišteno (`/v1/training/trial-status`);
 * ovdje su samo pravila prikaza koja iz toga slijede.
 */

export interface PlannedWorkoutRef {
  id: string;
  scheduledFor: string;
}

/**
 * Zaključani su svi treninzi od `lockedFrom` nadalje, redoslijedom rasporeda.
 * Server vraća samo PRVI zaključan trening — sve iza njega slijedi iz redoslijeda,
 * pa sučelje ne mora tražiti dodatnu rutu za svaki redak plana.
 */
export function lockedWorkoutIds(
  workouts: readonly PlannedWorkoutRef[],
  lockedFromWorkoutId: string | null | undefined,
): Set<string> {
  if (!lockedFromWorkoutId) return new Set();
  const ordered = [...workouts].sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
  const from = ordered.findIndex((w) => w.id === lockedFromWorkoutId);
  if (from < 0) return new Set();
  return new Set(ordered.slice(from).map((w) => w.id));
}

/** Probni pristup je „u tijeku” samo dok nije plaćen i dok ima preostalih treninga. */
export function isTrialActive(status: TrialStatus | undefined): boolean {
  return Boolean(status && !status.paid && status.trial && status.trial.remaining > 0);
}

/**
 * Hrvatski broji u tri oblika (1 / 2–4 / 5+), engleski u dva. Ključ se bira ovdje
 * da poruka „još N besplatnih treninga” ne bude gramatički kriva na brojevima
 * koje trener stvarno koristi.
 */
export function trialRemainingKey(remaining: number): string {
  return `trial.remaining${plural(remaining)}`;
}

/** Ista tri oblika za oznaku „prva {n} treninga besplatno” na kartici programa. */
export function trialBadgeKey(count: number): string {
  return `wizard.trialBadge${plural(count)}`;
}

function plural(value: number): 'One' | 'Few' | 'Many' {
  const n = Math.abs(Math.trunc(value));
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod10 === 1 && mod100 !== 11) return 'One';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'Few';
  return 'Many';
}
